import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["footerComponent", "onClose"];
import { useCallback, useMemo, useState } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { H2 } from 'foundations-components/transitional/components/H2';
import { Button } from 'foundations-components/transitional/components/Button';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { PanelBody } from 'foundations-components/transitional/components/PanelBody';
import { PanelFooter } from 'foundations-components/transitional/components/PanelFooter';
import { PanelHeader } from 'foundations-components/transitional/components/PanelHeader';
import { PanelSection, Panel } from 'foundations-components/transitional/components/Panel';
import { getObjectHref } from 'marketing-platform-lib/utils/objectHref';
import useCrmObjectName from 'marketing-platform-lib/hooks/useCrmObjectName';
import { capitalizeFirstLetter } from 'framework-asset-preview-lib/internal/shared/utils/assetPreview';
import { useAssetPreview } from 'framework-asset-preview-lib/internal/shared/providers/AssetPreviewProvider';
import useAssetPreviewTracking from 'framework-asset-preview-lib/internal/shared/hooks/useAssetPreviewTracking';
import { SIDEPANEL_WIDTH } from 'framework-asset-preview-lib/internal/shared/constants/assetPreview';
import cachedHttp from 'framework-asset-preview-lib/internal/shared/api/cachedHttp';
import AssetPreview from './AssetPreview';
import InlineEditPanelFooter from './InlineEditPanelFooter';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export default function ConnectedAssetPreviewSidepanel(_ref) {
  let {
      footerComponent,
      onClose
    } = _ref,
    assetPreviewProps = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    readOnly
  } = assetPreviewProps;
  const {
    objectType,
    previewType
  } = useAssetPreview();
  const [loadedCrmObject, setLoadedCrmObject] = useState(undefined);
  const {
    objectTypeNameSingular
  } = useCrmObjectName(objectType);
  const {
    trackAssetPreviewInteraction
  } = useAssetPreviewTracking();
  const footer = useMemo(() => {
    if (!loadedCrmObject) {
      return null;
    }
    if (readOnly != null && !readOnly) {
      return /*#__PURE__*/_jsx(InlineEditPanelFooter, {});
    }
    if (footerComponent) {
      return footerComponent;
    }
    const assetDetailsUrl = getObjectHref(loadedCrmObject);
    if (!assetDetailsUrl) {
      return null;
    }
    return /*#__PURE__*/_jsx(Button, {
      href: assetDetailsUrl,
      onClick: () => trackAssetPreviewInteraction({
        action: 'click view performance'
      }),
      use: "tertiary-light",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "frameworkAssetPreviewLib.sidepanel.actions.viewPerformance"
      })
    });
  }, [footerComponent, loadedCrmObject, readOnly, trackAssetPreviewInteraction]);
  const handleClose = useCallback(() => {
    cachedHttp.wipeAll();
    onClose();
  }, [onClose]);
  return /*#__PURE__*/_jsxs(Panel, {
    "data-test-id": "asset-preview-sidepanel",
    width: SIDEPANEL_WIDTH,
    children: [/*#__PURE__*/_jsx(DialogCloseButton, {
      "data-test-id": "asset-preview-sidepanel-close-button",
      onClick: handleClose
    }), /*#__PURE__*/_jsx(PanelHeader, {
      children: /*#__PURE__*/_jsx(H2, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "frameworkAssetPreviewLib.sidepanel.header",
          options: {
            assetName: capitalizeFirstLetter(objectTypeNameSingular)
          }
        })
      })
    }), /*#__PURE__*/_jsx(PanelBody, {
      children: /*#__PURE__*/_jsx(PanelSection, {
        padding: "0",
        children: /*#__PURE__*/_jsx(AssetPreview, Object.assign({}, assetPreviewProps, {
          onLoadCrmObject: setLoadedCrmObject,
          previewType: previewType
        }))
      })
    }), footer && /*#__PURE__*/_jsx(PanelFooter, {
      children: footer
    })]
  });
}